import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {PaymentMethodPipe} from '@gatewee/helpers/pipes/payment-method.pipe';
import {DomainSettingKey} from '@gatewee/types/domain-setting';
import {PaymentMethod} from '@gatewee/types/payment';
import {TranslateService} from '@ngx-translate/core';
import {TuiDay} from '@taiga-ui/cdk';
import {Subscription} from 'rxjs';

@Component({
  selector: 'gatewee-config-subscribe',
  templateUrl: './gatewee-config-subscribe.component.html',
  styleUrls: ['./gatewee-config-subscribe.component.css'],
  standalone: false,
})
export class GateweeConfigSubscribe implements OnInit, OnDestroy {
  constructor(public translate: TranslateService) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() settingsService: any;
  @Input() formSubscribe!: FormGroup;
  /** Plan/manual form that holds `paymentTiming` (prepaid/postpaid). */
  @Input() planForm?: FormGroup;

  today: TuiDay = TuiDay.fromLocalNativeDate(new Date());
  paymentMethods = Object.values(PaymentMethod)
    .filter(method => method !== PaymentMethod.CREDIT_CARD)
    .map(method => ({
      label: new PaymentMethodPipe().transform(method),
      value: method,
    }));

  private subs: Subscription[] = [];

  get isPixAutomatic(): boolean {
    return (
      this.formSubscribe?.get('paymentMethod')?.value ===
      PaymentMethod.PIX_AUTOMATIC
    );
  }

  get paymentTiming(): string {
    return String(this.planForm?.get('paymentTiming')?.value ?? '');
  }

  get showPrepaidHint(): boolean {
    return this.isPixAutomatic && this.paymentTiming === 'prepaid';
  }

  get showPostpaidHint(): boolean {
    return this.isPixAutomatic && this.paymentTiming === 'postpaid';
  }

  get showStartDateMin3DaysWarning(): boolean {
    if (!this.isPixAutomatic || this.paymentTiming !== 'postpaid') {
      return false;
    }
    const type = this.formSubscribe?.get('type')?.value;
    const dateCtrl =
      type === 'indefinite'
        ? this.formSubscribe?.get('billingDate')
        : this.formSubscribe?.get('initialDate');
    const raw = dateCtrl?.value;
    if (!raw) {
      return false;
    }
    const start = this.toLocalDate(raw);
    if (!start) {
      return false;
    }
    const min = new Date();
    min.setHours(0, 0, 0, 0);
    min.setDate(min.getDate() + 3);
    return start < min;
  }

  ngOnInit() {
    this.formSubscribe.get('type')?.setValue('limited');

    const paymentMethods = this.settingsService.get(
      'businessRules',
      DomainSettingKey.PAYMENT_METHODS,
    ) as Record<string, boolean>;
    this.paymentMethods = this.paymentMethods.filter(
      method => paymentMethods[method.value] === true,
    );

    const methodCtrl = this.formSubscribe.get('paymentMethod');
    if (methodCtrl) {
      this.subs.push(
        methodCtrl.valueChanges.subscribe(method => {
          if (method === PaymentMethod.PIX_AUTOMATIC) {
            this.formSubscribe
              .get('hasPreEmittedInvoices')
              ?.setValue(false, {emitEvent: false});
          }
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  private toLocalDate(raw: unknown): Date | null {
    if (!raw) {
      return null;
    }
    if (raw instanceof TuiDay) {
      return raw.toLocalNativeDate();
    }
    if (raw instanceof Date) {
      return raw;
    }
    if (typeof raw === 'string') {
      const d = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (
      typeof raw === 'object' &&
      raw !== null &&
      'year' in raw &&
      'month' in raw &&
      'day' in raw
    ) {
      const r = raw as {year: number; month: number; day: number};
      return new Date(r.year, r.month, r.day);
    }
    return null;
  }
}
